import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const GestionFormularioBecas = () => {
  // Usar siempre window.location.origin para que funcione con cualquier dominio
  const API = `${window.location.origin}/api`;
  
  const [formulario, setFormulario] = useState(null);
  const [preguntas, setPreguntas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editandoFormulario, setEditandoFormulario] = useState(false);
  const [mostrarNuevaPregunta, setMostrarNuevaPregunta] = useState(false);
  const [editandoPregunta, setEditandoPregunta] = useState(null);

  // Estado para el formulario
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: ''
  });

  // Estado para nueva pregunta
  const [nuevaPregunta, setNuevaPregunta] = useState({
    orden: 1,
    tipo: 'texto_corto',
    titulo: '',
    descripcion: '',
    opciones: [],
    requerida: true
  });

  // Configuración del editor Quill
  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'color': [] }, { 'background': [] }],
      ['clean']
    ]
  };

  const tiposPregunta = [
    { value: 'texto_corto', label: 'Texto Corto', icon: '📝' },
    { value: 'texto_largo', label: 'Texto Largo', icon: '📄' },
    { value: 'email', label: 'Email', icon: '📧' },
    { value: 'opcion_multiple', label: 'Opción Múltiple', icon: '☑️' },
    { value: 'verdadero_falso', label: 'Verdadero/Falso', icon: '✓✗' },
    { value: 'fecha', label: 'Fecha', icon: '📅' }
  ];

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      
      // Cargar formulario
      const resFormulario = await fetch(`${API}/becas/formulario`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (resFormulario.ok) {
        const dataFormulario = await resFormulario.json();
        setFormulario(dataFormulario);
        setFormData({
          titulo: dataFormulario.titulo,
          descripcion: dataFormulario.descripcion
        });

        // Cargar preguntas
        const resPreguntas = await fetch(`${API}/becas/formulario/preguntas`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (resPreguntas.ok) {
          const dataPreguntas = await resPreguntas.json();
          setPreguntas(dataPreguntas);
        }
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const guardarFormulario = async () => {
    try {
      const response = await fetch(`${API}/becas/formulario`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Formulario de becas guardado exitosamente');
        setEditandoFormulario(false);
        await cargarDatos();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al guardar formulario');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al guardar formulario');
    }
  };

  const agregarPregunta = async () => {
    if (!nuevaPregunta.titulo.trim()) {
      toast.error('El título de la pregunta es requerido');
      return;
    }

    try {
      // Calcular el siguiente orden
      const maxOrden = preguntas.length > 0 ? Math.max(...preguntas.map(p => p.orden)) : 0;
      const preguntaData = { ...nuevaPregunta, orden: maxOrden + 1 };

      const response = await fetch(`${API}/becas/formulario/preguntas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(preguntaData)
      });

      if (response.ok) {
        toast.success('Pregunta agregada exitosamente');
        setMostrarNuevaPregunta(false);
        setNuevaPregunta({
          orden: 1,
          tipo: 'texto_corto',
          titulo: '',
          descripcion: '',
          opciones: [],
          requerida: true
        });
        await cargarDatos();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al agregar pregunta');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al agregar pregunta');
    }
  };

  const editarPregunta = async () => {
    if (!editandoPregunta.titulo.trim()) {
      toast.error('El título de la pregunta es requerido');
      return;
    }

    try {
      const { id, ...preguntaData } = editandoPregunta;
      
      const response = await fetch(`${API}/becas/preguntas/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(preguntaData)
      });

      if (response.ok) {
        toast.success('Pregunta actualizada exitosamente');
        setEditandoPregunta(null);
        await cargarDatos();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al actualizar pregunta');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al actualizar pregunta');
    }
  };

  const eliminarPregunta = async (preguntaId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta pregunta?')) return;

    try {
      const response = await fetch(`${API}/becas/preguntas/${preguntaId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('Pregunta eliminada');
        await cargarDatos();
      } else {
        toast.error('Error al eliminar pregunta');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar pregunta');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          🎓 Gestión de Formulario de Becas
        </h1>
        <p className="text-gray-600 mt-2">Configura el formulario que los estudiantes llenarán para solicitar becas</p>
      </div>

      {/* Formulario Principal */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">📋 Información del Formulario</h2>
          {!editandoFormulario && formulario && (
            <button
              onClick={() => setEditandoFormulario(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Edit2 className="h-4 w-4" />
              Editar
            </button>
          )}
        </div>

        {editandoFormulario || !formulario ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título del Formulario *
              </label>
              <input
                type="text"
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ej: Solicitud de Beca Académica 2024"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción / Instrucciones *
              </label>
              <ReactQuill
                theme="snow"
                value={formData.descripcion}
                onChange={(value) => setFormData({ ...formData, descripcion: value })}
                modules={quillModules}
                className="bg-white"
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={guardarFormulario}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Save className="h-4 w-4" />
                Guardar Formulario
              </button>
              {formulario && (
                <button
                  onClick={() => {
                    setEditandoFormulario(false);
                    setFormData({
                      titulo: formulario.titulo,
                      descripcion: formulario.descripcion
                    });
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  <X className="h-4 w-4" />
                  Cancelar
                </button>
              )}
            </div>
          </div>
        ) : (
          <div>
            <h3 className="text-lg font-semibold mb-2">{formulario.titulo}</h3>
            <div
              className="text-gray-700 prose max-w-none"
              dangerouslySetInnerHTML={{ __html: formulario.descripcion }}
            />
          </div>
        )}
      </div>

      {/* Lista de Preguntas */}
      {formulario && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">❓ Preguntas del Formulario</h2>
            <button
              onClick={() => setMostrarNuevaPregunta(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <Plus className="h-4 w-4" />
              Nueva Pregunta
            </button>
          </div>

          {/* Nueva Pregunta Form */}
          {mostrarNuevaPregunta && (
            <div className="mb-6 p-4 bg-purple-50 border-2 border-purple-200 rounded-lg">
              <h3 className="font-semibold text-purple-900 mb-4">➕ Nueva Pregunta</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Pregunta *
                  </label>
                  <select
                    value={nuevaPregunta.tipo}
                    onChange={(e) => setNuevaPregunta({ ...nuevaPregunta, tipo: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    {tiposPregunta.map(tipo => (
                      <option key={tipo.value} value={tipo.value}>
                        {tipo.icon} {tipo.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Pregunta *
                  </label>
                  <input
                    type="text"
                    value={nuevaPregunta.titulo}
                    onChange={(e) => setNuevaPregunta({ ...nuevaPregunta, titulo: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Ej: ¿Por qué mereces esta beca?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descripción / Ayuda (opcional)
                  </label>
                  <ReactQuill
                    theme="snow"
                    value={nuevaPregunta.descripcion}
                    onChange={(value) => setNuevaPregunta({ ...nuevaPregunta, descripcion: value })}
                    modules={quillModules}
                    className="bg-white"
                  />
                </div>

                {nuevaPregunta.tipo === 'opcion_multiple' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Opciones (separadas por coma)
                    </label>
                    <input
                      type="text"
                      placeholder="Opción 1, Opción 2, Opción 3"
                      onChange={(e) => setNuevaPregunta({
                        ...nuevaPregunta,
                        opciones: e.target.value.split(',').map(o => o.trim()).filter(o => o)
                      })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="requerida"
                    checked={nuevaPregunta.requerida}
                    onChange={(e) => setNuevaPregunta({ ...nuevaPregunta, requerida: e.target.checked })}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                  />
                  <label htmlFor="requerida" className="text-sm text-gray-700">
                    Pregunta obligatoria
                  </label>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={agregarPregunta}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    <Save className="h-4 w-4" />
                    Guardar Pregunta
                  </button>
                  <button
                    onClick={() => {
                      setMostrarNuevaPregunta(false);
                      setNuevaPregunta({
                        orden: 1,
                        tipo: 'texto_corto',
                        titulo: '',
                        descripcion: '',
                        opciones: [],
                        requerida: true
                      });
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    <X className="h-4 w-4" />
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Lista de Preguntas */}
          {preguntas.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="h-12 w-12 mx-auto mb-2 text-gray-400" />
              <p>No hay preguntas todavía. Agrega la primera pregunta.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {preguntas.sort((a, b) => a.orden - b.orden).map((pregunta, index) => (
                <div
                  key={pregunta.id}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-purple-300 transition-colors"
                >
                  {editandoPregunta && editandoPregunta.id === pregunta.id ? (
                    // Formulario de edición
                    <div className="space-y-4 bg-purple-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-purple-900 mb-4">✏️ Editar Pregunta</h3>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tipo de Pregunta *
                        </label>
                        <select
                          value={editandoPregunta.tipo}
                          onChange={(e) => setEditandoPregunta({ ...editandoPregunta, tipo: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        >
                          {tiposPregunta.map(tipo => (
                            <option key={tipo.value} value={tipo.value}>
                              {tipo.icon} {tipo.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pregunta *
                        </label>
                        <input
                          type="text"
                          value={editandoPregunta.titulo}
                          onChange={(e) => setEditandoPregunta({ ...editandoPregunta, titulo: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Descripción / Ayuda (opcional)
                        </label>
                        <ReactQuill
                          theme="snow"
                          value={editandoPregunta.descripcion || ''}
                          onChange={(value) => setEditandoPregunta({ ...editandoPregunta, descripcion: value })}
                          modules={quillModules}
                          className="bg-white"
                        />
                      </div>

                      {editandoPregunta.tipo === 'opcion_multiple' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Opciones (separadas por coma)
                          </label>
                          <input
                            type="text"
                            value={editandoPregunta.opciones?.join(', ') || ''}
                            onChange={(e) => setEditandoPregunta({
                              ...editandoPregunta,
                              opciones: e.target.value.split(',').map(o => o.trim()).filter(o => o)
                            })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`requerida-edit-${pregunta.id}`}
                          checked={editandoPregunta.requerida}
                          onChange={(e) => setEditandoPregunta({ ...editandoPregunta, requerida: e.target.checked })}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                        />
                        <label htmlFor={`requerida-edit-${pregunta.id}`} className="text-sm text-gray-700">
                          Pregunta obligatoria
                        </label>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={editarPregunta}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          <Save className="h-4 w-4" />
                          Guardar Cambios
                        </button>
                        <button
                          onClick={() => setEditandoPregunta(null)}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                        >
                          <X className="h-4 w-4" />
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Vista normal
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </span>
                          <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                            {tiposPregunta.find(t => t.value === pregunta.tipo)?.label || pregunta.tipo}
                          </span>
                          {pregunta.requerida && (
                            <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded">
                              Obligatoria
                            </span>
                          )}
                        </div>
                        <h4 className="font-semibold text-gray-900">
                          <div 
                            className="prose prose-sm max-w-none prose-p:my-1 prose-headings:my-1 prose-ul:my-1 prose-ol:my-1"
                            dangerouslySetInnerHTML={{ __html: pregunta.titulo }}
                          />
                        </h4>
                        {pregunta.descripcion && (
                          <div
                            className="text-sm text-gray-600 mt-1 prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: pregunta.descripcion }}
                          />
                        )}
                        {pregunta.opciones && pregunta.opciones.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-gray-500">Opciones:</p>
                            <ul className="list-disc list-inside text-sm text-gray-600">
                              {pregunta.opciones.map((opcion, idx) => (
                                <li key={idx}>{opcion}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => setEditandoPregunta(pregunta)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Editar"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => eliminarPregunta(pregunta.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {!formulario && (
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 mx-auto mb-2 text-yellow-600" />
          <h3 className="font-semibold text-yellow-900 mb-2">No hay formulario de becas configurado</h3>
          <p className="text-yellow-700 mb-4">Crea el formulario primero para poder agregar preguntas</p>
        </div>
      )}
    </div>
  );
};

export default GestionFormularioBecas;
